import {
  effectiveNode,
  effectiveBackground,
  calendarAlignment,
} from "./text-styles";
import { backgroundRemoval } from "./image-background";
import { paginateFlow, type FlowPlacement } from "./flow";
import { layoutPageGroups, layoutGroupVisibility } from "./layout-groups";
import { mergeCopyfitLimits, type Copyfit } from "./copyfit";
import type { FrameClip } from "./frames";
import { renderTextTemplate } from "./text-template";
import {
  evaluateCalculation,
  calculationDependencies,
  CalculationError,
} from "./calculations";
import {
  planImposition,
  type ImpositionPlan,
  type SheetPlan,
  type ItemPlacement,
} from "./imposition";
import {
  productionGeometry,
  intersectRects,
  type Rect,
  type Segment,
} from "./production";
import { type FontRuntimeMap, type FontContext } from "./fonts";
import {
  type Project,
  type Page,
  type DesignNode,
  type Values,
  type Field,
  type OutputProfile,
  nodeSchema,
} from "./schema";
import {
  resolve,
  formatValue,
  condition,
  displayValue,
  qrPayload,
  type Context,
  type Diagnostic,
  type Resolution,
} from "./engine";
import {
  wrapText,
  calendarRuns,
  calendarHeight,
  calendarFitScale,
  richTextRuns,
  textFrameHeight,
  layoutRuns,
  layoutText,
  fitText,
  type Run,
  type PositionedRun,
} from "./typography";

export interface SceneNode extends DesignNode {
  text: string;
  assetId?: string;
  adjustments?: Record<string, unknown>;
  qrPayload?: string;
  qrForeground?: string;
  qrBackground?: string;
  sourceId: string;
  clip?: Rect;
  frameClip?: FrameClip;
  productionMark?: Segment;
  flowPlacement?: FlowPlacement;
  richRuns?: Run[];
  runs?: PositionedRun[];
}
export interface ScenePage {
  id: string;
  name: string;
  width: number;
  height: number;
  background: string | null;
  frame?: Page["frame"];
  artBox?: Page["artBox"];
  nodes: SceneNode[];
  production: Page["production"];
  trim: { x: number; y: number; width: number; height: number };
  bleed?: Rect;
  sheet?: SheetPlan;
}
export interface Scene {
  projectId: string;
  name: string;
  pages: ScenePage[];
  diagnostics: Diagnostic[];
  resolution: Resolution;
  metadata: Record<string, string>;
  imposition?: ImpositionPlan;
}
export const textLines = wrapText;
function rotatedBounds(n: DesignNode) {
  const angle = (n.rotation * Math.PI) / 180;
  if (n.imagePlacement && !n.clipImageToFrame) {
    const t = n.imagePlacement,
      width = n.width * t.scaleX,
      height = n.height * t.scaleY;
    const dx = t.offsetX + (width - n.width) / 2,
      dy = t.offsetY + (height - n.height) / 2;
    const w =
      Math.abs(width * Math.cos(angle)) + Math.abs(height * Math.sin(angle));
    const h =
      Math.abs(width * Math.sin(angle)) + Math.abs(height * Math.cos(angle));
    return {
      x:
        n.x + n.width / 2 + dx * Math.cos(angle) - dy * Math.sin(angle) - w / 2,
      y:
        n.y +
        n.height / 2 +
        dx * Math.sin(angle) +
        dy * Math.cos(angle) -
        h / 2,
      width: w,
      height: h,
    };
  }
  const w =
      Math.abs(n.width * Math.cos(angle)) +
      Math.abs(n.height * Math.sin(angle)),
    h =
      Math.abs(n.width * Math.sin(angle)) +
      Math.abs(n.height * Math.cos(angle));
  return {
    x: n.x + (n.width - w) / 2,
    y: n.y + (n.height - h) / 2,
    width: w,
    height: h,
  };
}
export function createScene(
  project: Project,
  input: Values = {},
  partId?: string,
  ctx: Context = {},
  output?: OutputProfile,
  fonts?: FontRuntimeMap,
): Scene {
  if (
    !project.pages.some((page) =>
      page.nodes.some(
        (node) => node.overflow === "shrink" && node.copyfit?.uniformGroup,
      ),
    )
  )
    return createScenePass(project, input, partId, ctx, output, fonts, {
      limits: new Map(),
      forced: new Map(),
      observed: new Map(),
    });
  const resolution = resolve(project, input, partId, ctx),
    limits = new Map<string, Copyfit>();
  for (const page of project.pages) {
    const visibleGroups = layoutGroupVisibility(
      page,
      (group) =>
        !group.visibleWhen || condition(group.visibleWhen, resolution.values),
    );
    for (const node of page.nodes) {
      const group = node.copyfit?.uniformGroup;
      if (
        !group ||
        node.overflow !== "shrink" ||
        node.hidden ||
        (node.layoutGroupId && !visibleGroups.get(node.layoutGroupId)) ||
        (node.fieldId && !resolution.visible.has(node.fieldId)) ||
        (node.visibleWhen && !condition(node.visibleWhen, resolution.values))
      )
        continue;
      const previous = limits.get(group);
      limits.set(
        group,
        previous ? mergeCopyfitLimits(previous, node.copyfit!) : node.copyfit!,
      );
    }
  }
  const forced = new Map<string, number>();
  for (let pass = 0; pass < 32; pass++) {
    const observed = new Map<string, number>();
    const scene = createScenePass(project, input, partId, ctx, output, fonts, {
      limits,
      forced,
      observed,
    });
    let changed = false;
    for (const [group, progress] of observed)
      if (progress > (forced.get(group) || 0) + 1e-9) {
        forced.set(group, progress);
        changed = true;
      }
    if (!changed) return scene;
  }
  throw new Error(
    "Uniform copyfit could not converge within its layout budget",
  );
}
interface CopyfitState {
  limits: Map<string, Copyfit>;
  forced: Map<string, number>;
  observed: Map<string, number>;
}
function createScenePass(
  project: Project,
  input: Values,
  partId: string | undefined,
  ctx: Context,
  output: OutputProfile | undefined,
  fonts: FontRuntimeMap | undefined,
  copyfitState: CopyfitState,
): Scene {
  const headerHeight =
      project.settings.headerHeight ?? project.settings.bandHeight,
    footerHeight = project.settings.footerHeight ?? project.settings.bandHeight;
  const resolution = resolve(project, input, partId, ctx),
    diagnostics: Diagnostic[] = [...(resolution.calculationErrors || [])],
    pages: ScenePage[] = [];
  const textContext = (node: DesignNode): FontContext | undefined => {
    if (!node.fontFaceId) return;
    const face = project.fontManifest.find((f) => f.id === node.fontFaceId);
    if (!face)
      throw new Error(`Missing managed font snapshot: ${node.fontFaceId}`);
    return { face, manifest: project.fontManifest, fonts: fonts || new Map() };
  };
  const fitNode = (
    node: SceneNode,
    runs: Run[],
    width: number,
    height: number,
  ) => {
    const group = node.copyfit?.uniformGroup,
      shared = group ? copyfitState.limits.get(group) : undefined;
    const settings =
      node.copyfit && shared
        ? {
            ...shared,
            maxLinesPerParagraph: node.copyfit.maxLinesPerParagraph,
          }
        : node.copyfit;
    const fitted = fitText(
      runs,
      width,
      height,
      node.fontSize,
      node.lineHeight,
      node.fontFamily,
      node.fontWeight,
      node.align,
      textContext(node),
      node.copyfit && node.placement === "flow"
        ? false
        : node.allowGlyphOverhang,
      node.textHeightMode === "font-box",
      settings,
      group ? copyfitState.forced.get(group) || 0 : 0,
    );
    if (group)
      copyfitState.observed.set(
        group,
        Math.max(copyfitState.observed.get(group) || 0, fitted.progress),
      );
    return fitted;
  };
  for (const source of project.pages) {
    const visibleGroups = layoutGroupVisibility(
      source,
      (group) =>
        !group.visibleWhen || condition(group.visibleWhen, resolution.values),
    );
    const nodes: SceneNode[] = [];
    const flowNodes: SceneNode[] = [];
    for (const raw of source.nodes) {
      if (
        raw.hidden ||
        (raw.layoutGroupId && !visibleGroups.get(raw.layoutGroupId)) ||
        (raw.fieldId && !resolution.visible.has(raw.fieldId)) ||
        (raw.visibleWhen && !condition(raw.visibleWhen, resolution.values))
      )
        continue;
      const field = resolution.fields.find((f) => f.id === raw.fieldId);
      let content = raw.textTemplate
        ? renderTextTemplate(
            raw.textTemplate,
            (id) => {
              const reference = resolution.fields.find((f) => f.id === id);
              return reference && resolution.visible.has(id)
                ? formatValue(reference, resolution.values[id])
                : "";
            },
            raw.type === "richtext",
          )
        : raw.content;
      if (raw.textCalculation) {
        const available = { ...resolution.values };
        for (const id of calculationDependencies(raw.textCalculation))
          if (!resolution.visible.has(id)) available[id] = null;
        try {
          content = displayValue(
            evaluateCalculation(
              raw.textCalculation,
              available,
              condition,
              displayValue,
            ),
          );
        } catch (error) {
          if (!(error instanceof CalculationError)) throw error;
          content = "";
          diagnostics.push({
            nodeId: raw.id,
            pageId: source.id,
            code: "CALCULATION_LIMIT",
            message: `${raw.name}: text calculation exceeds its limits`,
            severity: "error",
          });
        }
      }
      const n: SceneNode = {
        ...effectiveNode(project, raw, true),
        text: field ? formatValue(field, resolution.values[field.id]) : content,
        sourceId: raw.id,
      };
      const typography = textContext(n);
      if (typography) {
        n.fontFamily = typography.face.family;
        n.fontWeight = typography.face.weight;
      }
      if (n.type === "richtext" || field?.type === "richtext") {
        n.richRuns = richTextRuns(
          field ? String(resolution.values[field.id] ?? "") : content,
        );
        n.text = n.richRuns.map((r) => r.text).join("");
      }
      if (n.textFeatures && Object.keys(n.textFeatures).length)
        n.richRuns = (n.richRuns || [{ text: n.text }]).map((run) => ({
          ...run,
          textFeatures: { ...run.textFeatures, ...n.textFeatures },
        }));
      if (n.tabInterval !== undefined)
        n.richRuns = (n.richRuns || [{ text: n.text }]).map((run) => ({
          ...run,
          tabInterval: n.tabInterval,
        }));
      if (field?.type === "color" && /^#[0-9a-f]{6}$/i.test(n.text)) {
        if (n.fill !== "transparent") n.fill = n.text;
        else if (n.stroke !== "transparent") n.stroke = n.text;
      }
      if (field?.type === "qr" && (n.type === "qr" || n.type === "image")) {
        const answer =
          field?.type === "qr" ? resolution.values[field.id] : undefined;
        if (
          (typeof answer === "string" && /^asset_[a-f0-9]{64}$/.test(answer)) ||
          (answer &&
            typeof answer === "object" &&
            !Array.isArray(answer) &&
            typeof answer.assetId === "string" &&
            /^asset_[a-zA-Z0-9_-]+$/.test(answer.assetId))
        )
          n.type = "image";
        else n.type = "qr";
      }
      if (n.type === "qr") {
        n.qrPayload = field ? qrPayload(field, resolution.values) : n.text;
        n.qrForeground = field?.qr?.foreground || n.color;
        n.qrBackground = field?.qr?.background || "transparent";
      }
      if (n.type === "image" || n.type === "map") {
        const value = field ? resolution.values[field.id] : raw.content;
        if (typeof value === "string") {
          if (field?.type === "image-list") {
            n.assetId =
              resolution.choices[field.id].find((c) => c.value === value)
                ?.assetId ||
              (/^asset_[a-zA-Z0-9_-]+$/.test(value) ? value : undefined);
            if (value && !n.assetId)
              diagnostics.push({
                nodeId: n.id,
                pageId: source.id,
                fieldId: field.id,
                code: "IMAGE_CHOICE_UNMAPPED",
                message: `${field.label}: no image is assigned to this option`,
                severity: "error",
              });
          } else n.assetId = value;
        } else if (
          value &&
          typeof value === "object" &&
          !Array.isArray(value)
        ) {
          n.assetId = String(value.assetId || "");
          const removed = backgroundRemoval(value);
          n.adjustments = removed
            ? {
                ...(value.adjustments as Record<string, unknown>),
                backgroundMaskAssetId: removed.assetId,
              }
            : (value.adjustments as Record<string, unknown>);
        }
      }
      const sw = source.width || 612,
        sh = source.height || 792;
      if (n.anchor.includes("right"))
        n.x = sw - source.margin - n.width - raw.x;
      if (n.anchor.includes("bottom"))
        n.y = sh - source.margin - n.height - raw.y;
      if (n.anchor === "center") {
        n.x = (sw - n.width) / 2 + raw.x;
        n.y = (sh - n.height) / 2 + raw.y;
      }
      if (n.stretch === "horizontal" || n.stretch === "both")
        n.width = Math.max(1, sw - source.margin - n.x);
      if (n.stretch === "vertical" || n.stretch === "both")
        n.height = Math.max(1, sh - source.margin - n.y);
      if (
        n.type === "calendar" &&
        n.placement === "fixed" &&
        n.overflow === "shrink"
      ) {
        const runs = calendarRuns(
          n.text,
          n.width,
          n.fontFamily,
          n.fontWeight,
          typography,
          n.fontSize,
          n.lineHeight,
          calendarAlignment(n),
        );
        const scale = calendarFitScale(
          runs,
          n.width,
          n.height,
          n.fontFamily,
          n.fontWeight,
          typography,
          calendarAlignment(n),
        );
        if (scale < 1)
          n.fontSize = Math.max(Math.min(4, n.fontSize), n.fontSize * scale);
      }
      // The nearest group with an explicit override controls all text descendants.
      if (["text", "richtext"].includes(n.type)) {
        let groupId = n.layoutGroupId;
        const visited = new Set<string>();
        while (groupId && !visited.has(groupId)) {
          visited.add(groupId);
          const group = source.layoutGroups?.find(
            (item) => item.id === groupId,
          );
          if (group?.textAlign) {
            n.align = group.textAlign;
            break;
          }
          groupId = group?.parentId;
        }
      }
      if (n.placement === "flow") {
        flowNodes.push(n);
        continue;
      }
      const isText = ["text", "richtext"].includes(n.type);
      let needed = isText
        ? textFrameHeight(
            layoutText(
              n.richRuns || [{ text: n.text }],
              n.width,
              n.fontSize,
              n.lineHeight,
              n.fontFamily,
              n.fontWeight,
              n.align,
              typography,
              n.textHeightMode === "font-box",
            ),
            n.allowGlyphOverhang,
            n.textHeightMode === "font-box",
          )
        : 0;
      if (isText && n.contentHeight)
        n.height = Math.max(0.001, Math.min(n.height, needed));
      let copyfitOverflow = false;
      if (
        isText &&
        (needed > n.height || n.copyfit) &&
        n.overflow === "shrink"
      ) {
        const fitted = fitNode(
          n,
          n.richRuns || [{ text: n.text }],
          n.width,
          n.height,
        );
        n.fontSize = fitted.fontSize;
        n.lineHeight = fitted.lineHeight;
        n.runs = fitted.layout.runs;
        n.richRuns = fitted.runs;
        copyfitOverflow = !fitted.fits;
        needed = textFrameHeight(
          fitted.layout,
          n.allowGlyphOverhang,
          n.textHeightMode === "font-box",
        );
        if (n.copyfit && n.contentHeight)
          n.height = Math.max(0.001, Math.min(n.height, needed));
      }
      if (
        isText &&
        (needed > n.height || copyfitOverflow) &&
        n.overflow !== "continue"
      )
        diagnostics.push({
          nodeId: n.id,
          pageId: source.id,
          code: "TEXT_OVERFLOW",
          message: `${n.name}: ${project.settings.overflowMessage}`,
          severity: "warning",
        });
      nodes.push(n);
    }
    const fixedWidth = source.width,
      fixedHeight = source.height,
      top = source.margin + (project.settings.header ? headerHeight : 0),
      bottom = source.margin + (project.settings.footer ? footerHeight : 0);
    pages.push(
      ...paginateFlow(
        {
          ...source,
          background: effectiveBackground(project, source),
          artBox: output?.artBox ?? source.artBox,
          production:
            output?.includeProduction === false
              ? { bleed: 0, slug: 0, cropMarks: false }
              : output?.production || source.production,
        },
        layoutPageGroups(
          source,
          nodes,
          (group) => !!visibleGroups.get(group.id),
        ),
        flowNodes,
        {
          top,
          bottom,
          pageBudget: 100 - pages.length,
          diagnostics,
          overflowMessage: project.settings.overflowMessage,
          typography: textContext,
          fit: fitNode,
        },
      ),
    );
    // Auto-sized pages derive finite media from visible, rotated content, including negative coordinates.
    for (const pg of pages.filter(
      (p) =>
        p.id === source.id || p.id.startsWith(`${source.id}_continuation_`),
    )) {
      const bounds = pg.nodes.map(rotatedBounds);
      const minX = fixedWidth ? 0 : Math.min(0, ...bounds.map((b) => b.x)),
        minY = fixedHeight ? 0 : Math.min(0, ...bounds.map((b) => b.y));
      if (!fixedWidth) {
        pg.width = Math.max(
          1,
          Math.max(0, ...bounds.map((b) => b.x + b.width)) -
            minX +
            source.margin,
        );
        pg.nodes = pg.nodes.map((n) => ({
          ...n,
          x: n.x - minX,
          clip: n.clip ? { ...n.clip, x: n.clip.x - minX } : undefined,
        }));
      }
      if (!fixedHeight) {
        pg.height = Math.max(
          1,
          Math.max(top, ...bounds.map((b) => b.y + b.height)) - minY + bottom,
        );
        pg.nodes = pg.nodes.map((n) => ({
          ...n,
          y: n.y - minY,
          clip: n.clip ? { ...n.clip, y: n.clip.y - minY } : undefined,
        }));
      }
      pg.trim.width = pg.width;
      pg.trim.height = pg.height;
    }
  }
  if (pages.length > 100) throw new Error("Document exceeds 100-page limit");
  for (let i = 0; i < pages.length; i++) {
    const page = pages[i];
    const replace = (s: string) =>
      s
        .replace(/\{page\}/g, String(i + 1))
        .replace(/\{pages\}/g, String(pages.length))
        .replace(/\{pageName\}/g, page.name);
    if (project.settings.header && headerHeight > 0)
      page.nodes.push({
        ...effectiveNode(
          project,
          nodeSchema.parse({
            id: `header_${page.id}`,
            name: "Header",
            type: "text",
            x: 18,
            y: 8,
            width: Math.max(1, page.width - 36),
            height: headerHeight,
            fontSize: 9,
            styleId: project.settings.headerStyleId,
            typographyMode: project.settings.headerStyleId
              ? "inherit"
              : "direct",
          }),
          true,
        ),
        text: replace(project.settings.header),
        sourceId: "header",
      });
    if (project.settings.footer && footerHeight > 0)
      page.nodes.push({
        ...effectiveNode(
          project,
          nodeSchema.parse({
            id: `footer_${page.id}`,
            name: "Footer",
            type: "text",
            x: 18,
            y: page.height - footerHeight,
            width: Math.max(1, page.width - 36),
            height: footerHeight,
            fontSize: 9,
            align: "center",
            styleId: project.settings.footerStyleId,
            typographyMode: project.settings.footerStyleId
              ? "inherit"
              : "direct",
          }),
          true,
        ),
        text: replace(project.settings.footer),
        sourceId: "footer",
      });
    if (output?.watermark)
      page.nodes.push({
        ...nodeSchema.parse({
          id: `watermark_${page.id}`,
          name: "Watermark",
          type: "text",
          x: 0,
          y: page.height / 2 - 20,
          width: page.width,
          height: 60,
          fontSize: 36,
          align: "center",
          rotation: -30,
          opacity: 0.15,
        }),
        text: output.watermark,
        sourceId: "watermark",
      });
  }
  for (const page of pages)
    for (const n of page.nodes)
      if (n.type === "calendar") {
        n.runs = calendarRuns(
          n.text,
          n.width,
          n.fontFamily,
          n.fontWeight,
          textContext(n),
          n.fontSize,
          n.lineHeight,
          calendarAlignment(n),
        );
        if (
          calendarFitScale(
            n.runs,
            n.width,
            n.height,
            n.fontFamily,
            n.fontWeight,
            textContext(n),
            calendarAlignment(n),
          ) <
            1 - 1e-7 &&
          !diagnostics.some(
            (d) =>
              d.nodeId === n.sourceId &&
              d.pageId === page.id &&
              d.code === "CALENDAR_OVERFLOW",
          )
        )
          diagnostics.push({
            nodeId: n.sourceId,
            pageId: page.id,
            code: "CALENDAR_OVERFLOW",
            message: `${n.name}: ${project.settings.overflowMessage}`,
            severity: "warning",
          });
      } else if (["text", "richtext"].includes(n.type) && !n.runs)
        n.runs = layoutRuns(
          n.richRuns || [{ text: n.text }],
          n.width,
          n.fontSize,
          n.lineHeight,
          n.fontFamily,
          n.fontWeight,
          n.align,
          textContext(n),
          n.textHeightMode === "font-box",
        );
  for (const page of pages)
    for (const node of page.nodes) {
      if (!["text", "richtext"].includes(node.type)) continue;
      if (node.baselineMode === "ascent" && node.runs?.length) {
        const first = node.runs.filter((run) => run.line === 0);
        const ascent = Math.max(
          ...first.map(
            (run) => (run.fontAscent ?? 1) * (run.size || node.fontSize),
          ),
        );
        const shift =
          node.textHeightMode === "font-box"
            ? layoutText(
                node.richRuns || [{ text: node.text }],
                node.width,
                node.fontSize,
                node.lineHeight,
                node.fontFamily,
                node.fontWeight,
                node.align,
                textContext(node),
                true,
              ).ascentShift
            : first.length
              ? first[0].y - ascent
              : 0;
        node.runs = node.runs.map((run) => ({ ...run, y: run.y - shift }));
      }
      if (!node.verticalAlign || node.verticalAlign === "top") continue;
      if (node.verticalAlign === "bottom-baseline") {
        const lastBaseline = Math.max(
          0,
          ...(node.runs || []).map((run) => run.y),
        );
        const shift = Math.max(0, node.height - lastBaseline);
        node.runs = node.runs?.map((run) => ({ ...run, y: run.y + shift }));
        continue;
      }
      const layout = layoutText(
        node.richRuns || [{ text: node.text }],
        node.width,
        node.fontSize,
        node.lineHeight,
        node.fontFamily,
        node.fontWeight,
        node.align,
        textContext(node),
        node.textHeightMode === "font-box",
      );
      const offset =
        Math.max(
          0,
          node.height -
            (node.textHeightMode === "font-box"
              ? layout.fontBoxHeight
              : layout.height),
        ) * (node.verticalAlign === "middle" ? 0.5 : 1);
      node.runs = node.runs?.map((run) => ({ ...run, y: run.y + offset }));
    }
  for (const page of pages)
    for (const node of page.nodes)
      if (node.textBaselineShift && ["text", "richtext"].includes(node.type))
        node.runs = node.runs?.map((run) => ({
          ...run,
          y: run.y - node.textBaselineShift!,
        }));
  for (const page of pages)
    for (const node of page.nodes)
      if (
        node.richRuns?.some((run) => run.tabInterval !== undefined) &&
        node.runs?.some(
          (run) => run.x < -1e-7 || run.x + run.width > node.width + 1e-7,
        ) &&
        !diagnostics.some(
          (item) =>
            item.code === "TEXT_OVERFLOW" &&
            (item.nodeId === node.id || item.nodeId === node.sourceId),
        )
      )
        diagnostics.push({
          nodeId: node.sourceId,
          pageId: page.id,
          code: "TEXT_OVERFLOW",
          message: `${node.name}: ${project.settings.overflowMessage}`,
          severity: "warning",
        });
  const scene = {
    projectId: project.id,
    name: project.name,
    pages,
    diagnostics,
    resolution,
    metadata: project.settings.metadata,
  };
  for (const pg of pages)
    if (pg.frame)
      for (const node of pg.nodes)
        node.frameClip = {
          frame: pg.frame,
          x: 0,
          y: 0,
          width: pg.width,
          height: pg.height,
          rotation: 0,
        };
  return output?.imposition.enabled ? imposeScene(scene, output) : scene;
}
function markNode(
  segment: Segment,
  id: string,
  strokeWidth: number,
  stroke = "#000000",
): SceneNode {
  const x = Math.min(segment.x1, segment.x2),
    y = Math.min(segment.y1, segment.y2),
    width = Math.max(0.1, Math.abs(segment.x2 - segment.x1)),
    height = Math.max(0.1, Math.abs(segment.y2 - segment.y1));
  // Canonical mark direction prevents rasterizer endpoint coverage changing
  // between equivalent left/top and right/bottom crop marks.
  const forward =
    segment.x1 < segment.x2 ||
    (segment.x1 === segment.x2 && segment.y1 <= segment.y2);
  const start = forward ? [segment.x1, segment.y1] : [segment.x2, segment.y2];
  const end = forward ? [segment.x2, segment.y2] : [segment.x1, segment.y1];
  return {
    ...nodeSchema.parse({
      id,
      name: "Crop mark",
      type: "path",
      x,
      y,
      width,
      height,
      pathWidth: width,
      pathHeight: height,
      path: `M${start[0] - x} ${start[1] - y}L${end[0] - x} ${end[1] - y}`,
      stroke,
      strokeWidth,
    }),
    text: "",
    sourceId: "crop-mark",
    productionMark: { x1: start[0], y1: start[1], x2: end[0], y2: end[1] },
  };
}
function placedRect(rect: Rect, source: ScenePage, item: ItemPlacement): Rect {
  return item.rotation
    ? {
        x: item.trim.x + source.height - rect.y - rect.height,
        y: item.trim.y + rect.x,
        width: rect.height,
        height: rect.width,
      }
    : {
        x: item.trim.x + rect.x,
        y: item.trim.y + rect.y,
        width: rect.width,
        height: rect.height,
      };
}
export function imposeScene(scene: Scene, output: OutputProfile): Scene {
  if (!output.imposition.enabled) return scene;
  const plan = planImposition(scene.pages, output.imposition),
    pages: ScenePage[] = [];
  for (const sheet of plan.sheets) {
    const source = scene.pages.find((p) => p.id === sheet.sourcePageId)!,
      nodes: SceneNode[] = [];
    for (const item of sheet.items) {
      const frameClip: FrameClip | undefined = source.frame
        ? {
            frame: source.frame,
            x: item.trim.x + (item.rotation ? source.height : 0),
            y: item.trim.y,
            width: source.width,
            height: source.height,
            rotation: item.rotation,
          }
        : undefined;
      if (source.background)
        nodes.push({
          ...nodeSchema.parse({
            id: `sheet_bg_${sheet.chunk}_${sheet.sourceIndex}_${item.copyIndex}`,
            name: "Item background",
            type: "rect",
            ...item.bleed,
            fill: source.background,
          }),
          text: "",
          sourceId: "background",
          clip: item.bleed,
          frameClip,
        });
      for (const n of source.nodes)
        nodes.push({
          ...n,
          id: `${n.id}_copy_${item.copyIndex}`,
          x:
            item.trim.x +
            (item.rotation
              ? source.height - n.y - n.height / 2 - n.width / 2
              : n.x),
          y:
            item.trim.y +
            (item.rotation ? n.x + n.width / 2 - n.height / 2 : n.y),
          rotation: n.rotation + item.rotation,
          frameClip,
          clip: n.clip
            ? intersectRects(item.bleed, placedRect(n.clip, source, item))
            : item.bleed,
        });
      item.marks.forEach((segment, i) =>
        nodes.push(
          markNode(
            segment,
            `mark_${sheet.id}_${item.copyIndex}_${i}`,
            output.imposition.cropMarks.strokeWidth,
          ),
        ),
      );
    }
    pages.push({
      id: sheet.id,
      name: sheet.name,
      width: sheet.width,
      height: sheet.height,
      background: null,
      nodes,
      production: { bleed: 0, slug: 0, cropMarks: false },
      artBox: output.artBox,
      trim: { x: 0, y: 0, width: sheet.width, height: sheet.height },
      bleed: { x: 0, y: 0, width: sheet.width, height: sheet.height },
      sheet,
    });
  }
  return { ...scene, pages, imposition: plan };
}
export function productionPage(page: ScenePage): ScenePage {
  const geometry = productionGeometry(page.width, page.height, page.production),
    artworkClip =
      page.production.clipArtworkToBleed === false
        ? { x: 0, y: 0, width: geometry.width, height: geometry.height }
        : geometry.bleed,
    offsetX = geometry.trim.x,
    offsetY = geometry.trim.y;
  const nodes: SceneNode[] = page.nodes.map((n) => {
    const moved = {
      ...n,
      x: n.x + offsetX,
      y: n.y + offsetY,
      frameClip: n.frameClip
        ? {
            ...n.frameClip,
            x: n.frameClip.x + offsetX,
            y: n.frameClip.y + offsetY,
          }
        : undefined,
      clip: n.clip
        ? intersectRects(artworkClip, {
            ...n.clip,
            x: n.clip.x + offsetX,
            y: n.clip.y + offsetY,
          })
        : artworkClip,
    };
    // Intersect plain fills as geometry. An extra antialiased clip at a fill
    // edge can leave a faint seam, unlike the equivalent cropped rectangle.
    if (
      moved.type === "rect" &&
      !moved.radius &&
      moved.rotation % 360 === 0 &&
      (moved.stroke === "transparent" || !moved.strokeWidth)
    ) {
      const clipped = intersectRects(moved, moved.clip);
      if (clipped.width > 0 && clipped.height > 0)
        return { ...moved, ...clipped, clip: undefined };
    }
    return moved;
  });
  let markIndex = 0;
  for (const group of geometry.markGroups) {
    if (group.combined && group.segments.length) {
      const segments = group.segments,
        anchor = segments[0];
      const x = Math.min(...segments.flatMap((s) => [s.x1, s.x2]));
      const y = Math.min(...segments.flatMap((s) => [s.y1, s.y2]));
      const width = Math.max(...segments.flatMap((s) => [s.x1, s.x2])) - x;
      const height = Math.max(...segments.flatMap((s) => [s.y1, s.y2])) - y;
      const number = (value: number) => +value.toFixed(6);
      const path = segments
        .map(
          (s) =>
            `M${number(s.x1 - anchor.x1)} ${number(anchor.y1 - s.y1)}L${number(s.x2 - anchor.x1)} ${number(anchor.y1 - s.y2)}`,
        )
        .join("");
      const add = (id: string, strokeWidth: number, stroke: string) =>
        nodes.push({
          ...nodeSchema.parse({
            id,
            name: `${group.kind} marks`,
            type: "path",
            x,
            y,
            width,
            height,
            path,
            pathViewport: {
              x: x - anchor.x1,
              y: anchor.y1 - y - height,
              width,
              height,
              yAxis: "up",
            },
            stroke,
            strokeWidth,
          }),
          sourceId: id,
          text: "",
        });
      if (group.outlineWidth)
        add(
          `mark_outline_${page.id}_${group.kind}`,
          group.strokeWidth + 2 * group.outlineWidth,
          "#ffffff",
        );
      add(`mark_${page.id}_${group.kind}`, group.strokeWidth, "#000000");
      markIndex += group.segments.length;
    } else
      for (const segment of group.segments) {
        if (group.outlineWidth)
          nodes.push(
            markNode(
              segment,
              `mark_outline_${page.id}_${markIndex}`,
              group.strokeWidth + 2 * group.outlineWidth,
              "#ffffff",
            ),
          );
        nodes.push(
          markNode(segment, `mark_${page.id}_${markIndex}`, group.strokeWidth),
        );
        markIndex++;
      }
  }
  return {
    ...page,
    width: geometry.width,
    height: geometry.height,
    nodes,
    trim: geometry.trim,
    bleed: geometry.bleed,
  };
}
